import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  HolderOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Button, Card, Input, Popconfirm, Space, Tooltip } from 'antd';
import {
  CreateFormTemplateQuestionRequest,
  FormAnswerRestriction,
} from '../../models/TaskForm/TaskForm';
import {
  AnswerRestrictionOption,
  AnswerRestrictionSelect,
} from './AnswerRestrictionSelect';
import {
  EditableTemplateQuestion,
  reorderTemplateQuestions,
} from './useTemplateQuestions';
import styles from './templateEditorModal.module.css';

interface Props {
  questions: EditableTemplateQuestion[];
  duplicateQuestionClientIds?: string[];
  readonly?: boolean;
  answerRestrictionOptions?: AnswerRestrictionOption[];
  fallbackAnswerRestrictionOptions?: AnswerRestrictionOption[];
  onAddQuestion: () => void;
  onUpdateQuestion: (
    index: number,
    patch: Partial<CreateFormTemplateQuestionRequest>,
  ) => void;
  onMoveQuestion: (index: number, step: -1 | 1) => void;
  onReorderQuestion: (fromIndex: number, toIndex: number) => void;
  onCopyQuestion: (index: number) => void;
  onDeleteQuestion: (index: number) => void;
}

export interface TemplateQuestionListHandle {
  addQuestionAndScroll: () => void;
}

export const TemplateQuestionList = forwardRef<
  TemplateQuestionListHandle,
  Props
>(
  (
    {
      questions,
      duplicateQuestionClientIds = [],
      readonly = false,
      answerRestrictionOptions,
      fallbackAnswerRestrictionOptions,
      onAddQuestion,
      onUpdateQuestion,
      onMoveQuestion,
      onReorderQuestion,
      onCopyQuestion,
      onDeleteQuestion,
    },
    ref,
  ) => {
    const questionsScrollAreaRef = useRef<HTMLDivElement | null>(null);
    const [dragSourceClientId, setDragSourceClientId] = useState<string | null>(
      null,
    );
    const [dragOverClientId, setDragOverClientId] = useState<string | null>(
      null,
    );

    const answerTypeHints: Record<FormAnswerRestriction, string> = {
      [FormAnswerRestriction.YesNo]: 'Скажите «Да» или «Нет»',
      [FormAnswerRestriction.Number_1_5]: 'Назовите число от одного до пяти',
      [FormAnswerRestriction.Number_1_10]: 'Назовите число от одного до десяти',
      [FormAnswerRestriction.Number]:
        'Назовите конкретное число без дополнительных слов',
      [FormAnswerRestriction.Text]:
        'Ответьте в свободной форме короткой фразой',
      [FormAnswerRestriction.Date]: 'Назовите дату в формате день.месяц.год',
    };

    const typedQuestions = questions as EditableTemplateQuestion[];
    const duplicateClientIdSet = useMemo(
      () => new Set(duplicateQuestionClientIds),
      [duplicateQuestionClientIds],
    );

    const getSourceIndex = (clientId: string) =>
      typedQuestions.findIndex((q) => q.clientId === clientId);

    const scrollToLastQuestion = () => {
      const scrollArea = questionsScrollAreaRef.current;

      if (!scrollArea) {
        return;
      }

      const questionCards = scrollArea.querySelectorAll<HTMLElement>(
        '[data-question-client-id]',
      );
      const lastQuestionCard = questionCards[questionCards.length - 1];

      lastQuestionCard?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    };

    const handleAddQuestion = useCallback(() => {
      onAddQuestion();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToLastQuestion();
        });
      });
    }, [onAddQuestion]);

    useImperativeHandle(
      ref,
      () => ({ addQuestionAndScroll: handleAddQuestion }),
      [handleAddQuestion],
    );

    useEffect(() => {
      if (readonly) {
        setDragSourceClientId(null);
        setDragOverClientId(null);
      }
    }, [readonly]);

    useEffect(() => {
      if (dragSourceClientId === null) {
        return undefined;
      }

      const handleEscapeCancel = (event: KeyboardEvent) => {
        if (event.key !== 'Escape') {
          return;
        }

        setDragSourceClientId(null);
        setDragOverClientId(null);
      };

      window.addEventListener('keydown', handleEscapeCancel);

      return () => {
        window.removeEventListener('keydown', handleEscapeCancel);
      };
    }, [dragSourceClientId]);

    const previewQuestions = useMemo(() => {
      if (dragSourceClientId === null || dragOverClientId === null) {
        return typedQuestions;
      }

      const fromIndex = getSourceIndex(dragSourceClientId);
      const toIndex = getSourceIndex(dragOverClientId);

      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
        return typedQuestions;
      }

      return reorderTemplateQuestions(typedQuestions, fromIndex, toIndex);
    }, [dragOverClientId, dragSourceClientId, typedQuestions]);

    const handleDrop = () => {
      if (dragSourceClientId === null || dragOverClientId === null) {
        setDragSourceClientId(null);
        setDragOverClientId(null);
        return;
      }

      const fromIndex = getSourceIndex(dragSourceClientId);
      const toIndex = getSourceIndex(dragOverClientId);

      if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
        onReorderQuestion(fromIndex, toIndex);
      }

      setDragSourceClientId(null);
      setDragOverClientId(null);
    };

    const handleAutoScroll = (clientY: number) => {
      const scrollArea = questionsScrollAreaRef.current;

      if (!scrollArea) {
        return;
      }

      const { top, bottom } = scrollArea.getBoundingClientRect();
      const edgeOffset = 56;
      const minStep = 2;
      const maxStep = 12;

      const getStep = (distanceToEdge: number) => {
        const normalizedDistance = Math.min(
          edgeOffset,
          Math.max(0, distanceToEdge),
        );
        const intensity = (edgeOffset - normalizedDistance) / edgeOffset;

        return Math.ceil(minStep + (maxStep - minStep) * intensity);
      };

      if (clientY < top + edgeOffset) {
        scrollArea.scrollTop -= getStep(clientY - top);
        return;
      }

      if (clientY > bottom - edgeOffset) {
        scrollArea.scrollTop += getStep(bottom - clientY);
      }
    };

    return (
      <Space direction="vertical" className={styles.fullWidth} size="middle">
        <div className={styles.questionsHeader}>
          <span className={styles.questionsTitle}>
            Вопросы ({questions.length})
          </span>
        </div>

        <div
          className={styles.questionsListScrollArea}
          ref={questionsScrollAreaRef}
          onDragOver={(event) => {
            if (readonly || dragSourceClientId === null) {
              return;
            }

            event.preventDefault();
            handleAutoScroll(event.clientY);
          }}
        >
          <Space
            direction="vertical"
            className={styles.fullWidth}
            size="middle"
          >
            {previewQuestions.map((question, index) => {
              const sourceIndex = getSourceIndex(question.clientId);

              return (
                <Card
                  key={question.clientId}
                  size="small"
                  className={`${styles.questionCard} ${
                    dragSourceClientId === question.clientId
                      ? styles.questionCardDragging
                      : ''
                  }`}
                  data-question-client-id={question.clientId}
                  data-testid={`template-question-card-${index}`}
                  onDragOver={(event) => {
                    if (readonly || dragSourceClientId === null) {
                      return;
                    }

                    event.preventDefault();

                    if (question.clientId === dragSourceClientId) {
                      return;
                    }

                    if (dragOverClientId !== question.clientId) {
                      setDragOverClientId(question.clientId);
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    handleDrop();
                  }}
                >
                  <div className={styles.questionRow}>
                    <div className={styles.questionLeftRail}>
                      {!readonly ? (
                        <button
                          type="button"
                          className={styles.dragHandleButton}
                          aria-label="Перетащить вопрос"
                          title="Перетащить вопрос"
                          draggable
                          data-testid={`template-question-drag-handle-${index}`}
                          onDragStart={(event) => {
                            event.dataTransfer.effectAllowed = 'move';
                            setDragSourceClientId(question.clientId);
                            setDragOverClientId(question.clientId);
                          }}
                          onDragEnd={(event) => {
                            if (event.dataTransfer.dropEffect === 'none') {
                              handleDrop();
                              return;
                            }

                            setDragSourceClientId(null);
                            setDragOverClientId(null);
                          }}
                        >
                          <HolderOutlined />
                        </button>
                      ) : (
                        <span className={styles.dragHandlePlaceholder}>
                          <HolderOutlined />
                        </span>
                      )}
                      <span className={styles.questionOrdinal}>
                        {index + 1}
                      </span>

                      {!readonly && (
                        <div className={styles.moveControlsColumn}>
                          <Tooltip title="Переместить вопрос вверх">
                            <Button
                              data-testid={`template-question-move-up-${index}`}
                              icon={<UpOutlined />}
                              size="small"
                              className={styles.iconGhostButton}
                              aria-label="Переместить вопрос вверх"
                              title="Переместить вопрос вверх"
                              onClick={() =>
                                sourceIndex >= 0 &&
                                onMoveQuestion(sourceIndex, -1)
                              }
                              disabled={index === 0 || sourceIndex < 0}
                            />
                          </Tooltip>
                          <Tooltip title="Переместить вопрос вниз">
                            <Button
                              data-testid={`template-question-move-down-${index}`}
                              icon={<DownOutlined />}
                              size="small"
                              className={styles.iconGhostButton}
                              aria-label="Переместить вопрос вниз"
                              title="Переместить вопрос вниз"
                              onClick={() =>
                                sourceIndex >= 0 &&
                                onMoveQuestion(sourceIndex, 1)
                              }
                              disabled={index === previewQuestions.length - 1}
                            />
                          </Tooltip>
                        </div>
                      )}
                    </div>

                    <div className={styles.questionTextColumn}>
                      <div className={styles.questionInputWrapper}>
                        <Input.TextArea
                          value={question.question}
                          onChange={(event) =>
                            sourceIndex >= 0 &&
                            onUpdateQuestion(sourceIndex, {
                              question: event.target.value,
                            })
                          }
                          disabled={readonly}
                          autoSize={{ minRows: 2, maxRows: 4 }}
                          placeholder="Текст вопроса"
                          status={
                            duplicateClientIdSet.has(question.clientId)
                              ? 'error'
                              : undefined
                          }
                          data-testid={`template-question-text-input-${index}`}
                        />
                        {duplicateClientIdSet.has(question.clientId) && (
                          <Tooltip title="Вопрос дублируется">
                            <span
                              className={styles.questionValidationWarningIcon}
                              data-testid={`template-question-duplicate-warning-${index}`}
                            >
                              <ExclamationCircleOutlined />
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <span className={styles.answerTypeHint}>
                        {answerTypeHints[question.restriction]}
                      </span>
                    </div>

                    <div className={styles.questionRestrictionColumn}>
                      <span className={styles.answerTypeLabel}>Тип ответа</span>
                      <AnswerRestrictionSelect
                        value={question.restriction}
                        options={answerRestrictionOptions}
                        fallbackOptions={fallbackAnswerRestrictionOptions}
                        disabled={readonly}
                        dataTestId={`template-answer-restriction-select-${index}`}
                        onChange={(value) =>
                          sourceIndex >= 0 &&
                          onUpdateQuestion(sourceIndex, { restriction: value })
                        }
                      />
                    </div>

                    {!readonly && (
                      <div className={styles.questionActionRail}>
                        <Tooltip title="Удалить вопрос">
                          <Popconfirm
                            title="Удалить вопрос?"
                            okText="Удалить"
                            cancelText="Отмена"
                            onConfirm={() =>
                              sourceIndex >= 0 && onDeleteQuestion(sourceIndex)
                            }
                          >
                            <Button
                              data-testid={`template-question-delete-${index}`}
                              icon={<DeleteOutlined />}
                              size="small"
                              className={`${styles.iconGhostButton} ${styles.deleteQuestionButton}`}
                              aria-label="Удалить вопрос"
                              title="Удалить вопрос"
                              disabled={previewQuestions.length === 1}
                            />
                          </Popconfirm>
                        </Tooltip>
                        <Tooltip title="Копировать вопрос">
                          <Button
                            data-testid={`template-question-copy-${index}`}
                            icon={<CopyOutlined />}
                            size="small"
                            className={styles.iconGhostButton}
                            aria-label="Копировать вопрос"
                            title="Копировать вопрос"
                            onClick={() =>
                              sourceIndex >= 0 && onCopyQuestion(sourceIndex)
                            }
                          />
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </Space>
        </div>
      </Space>
    );
  },
);

TemplateQuestionList.displayName = 'TemplateQuestionList';
